import React, { useMemo } from 'react';
import { Card } from '@app/screens/rewards/components/Card';
import { Button, Center, HStack, Spinner, Text } from '@chakra-ui/react';
import Countdown from 'react-countdown';

import { Icon } from '@app/components/Icon';
import { useEpochDetails } from '@app/hooks/ryo/useEpochDetails';
import { ListLoader } from '@app/screens/pools/components/ListLoader';
import { PendingTokenItem } from '@app/screens/rewards/components/PendingTokenItem';
import { useMultipleTokensQuery } from '@app/hooks/v3/useMultipleTokensQuery';
import { useNativePriceQuery } from '@app/hooks/v3/useNativePriceQuery';
import { formatUSD } from '@app/helpers/formatUSD';
import { useVaults } from '@app/hooks/plugins/useVaults';
import { PendingTokenItemPreview } from '../PendingRewardsWidgetPreview';
import { useConfig } from '@app/config';
import uniqBy from 'lodash/uniqBy';

export const EpochWidget = () => {
  const {
    nextCycleStartTime,
    isLoading,
    distributedTokensIndexes,
    distributedTokens
  } = useEpochDetails();
  const { data: bundle } = useNativePriceQuery();

  const config = useConfig();

  const { data } = useMultipleTokensQuery({
    tokenIds: distributedTokens
      ?.map(token => {
        let _address;

        if (
          token.address.toLowerCase() === config?.CONTRACTS.X_KIM.toLowerCase()
        ) {
          _address = config?.CONTRACTS.KIM.toLowerCase();
        } else {
          _address = token.address.toLowerCase();
        }

        return `"${_address}"`;
      })
      .join(',')
  });

  const { data: vaults } = useVaults();

  const totalUsdAmount = useMemo(() => {
    if (!data || !bundle || !distributedTokens) {
      return;
    }

    return distributedTokens.reduce((r, item) => {
      const tokenData = data.find(t => {
        if (
          item.address.toLowerCase() === config?.CONTRACTS.X_KIM?.toLowerCase()
        ) {
          return t.id.toLowerCase() === config?.CONTRACTS.KIM?.toLowerCase();
        }

        return t.id.toLowerCase() === item.address.toLowerCase();
      });

      const vaultData = vaults?.find(
        v => v.lpToken.id.toLowerCase() === item.address.toLowerCase()
      );

      if (vaultData) {
        const usdAmount =
          Number(vaultData.lpToken.lpTokenUSD) * +item.pendingAmount;

        return usdAmount + r;
      }

      if (!tokenData) {
        return r;
      }

      const tokenUSDValue =
        Number(tokenData.derivedMatic) * Number(bundle.maticPriceUSD);

      return tokenUSDValue * +item.pendingAmount + r;
    }, 0);
  }, [bundle, data, distributedTokens, vaults, config]);

  return (
    <Card
      title="Epoch"
      titleAction={
        <Button
          variant="tertiary"
          onClick={() => {}}
          display="none"
          sx={{
            color: 'orange',
            bg: 'gray.2',
            width: 'fit-content',
            fontSize: '14px',
            padding: '14px 28px'
          }}
        >
          <HStack alignItems="center">
            <Icon name="calc" style={{ width: '20px', height: '20px' }} />
            <Text>Calculator</Text>
          </HStack>
        </Button>
      }
    >
      <HStack gap="22px" flexWrap={['wrap', 'unset']}>
        {isLoading ? (
          <ListLoader />
        ) : (
          distributedTokensIndexes?.map(index => {
            return <PendingTokenItem key={index} index={index} />;
          })
        )}
      </HStack>
      <Center
        sx={{
          borderRadius: '8px',
          background: 'gray.10',
          padding: '14px 12px',
          width: '100%',
          marginTop: '24px'
        }}
      >
        <HStack flexDirection={['column', 'row']}>
          {isLoading ? (
            <Spinner color="orange" width={8} height={8} />
          ) : (
            nextCycleStartTime !== undefined && (
              <>
                <Icon
                  name="infoCircle"
                  style={{ color: 'var(--chakra-colors-orange)' }}
                />
                <Countdown
                  date={new Date(nextCycleStartTime * 1000)}
                  renderer={({ days, hours, minutes, seconds, completed }) => {
                    if (completed) {
                      return (
                        <Text fontSize="18px" fontWeight={400} color="white">
                          Time is up!
                        </Text>
                      );
                    } else {
                      return (
                        <HStack
                          flexDirection={['column', 'row']}
                          alignItems={['flex-start', 'center']}
                        >
                          <Text fontSize="18px" fontWeight={400} color="white">
                            Next epoch will start in
                          </Text>
                          <Text fontSize="18px" fontWeight={400} color="orange">
                            {days ? `${days}D ` : ''}
                            {hours ? `${hours}h ` : ''}
                            {minutes ? `${minutes}m ` : ''}
                            {seconds ? `${seconds}s` : ''}
                          </Text>
                          {totalUsdAmount && (
                            <Text
                              fontSize="18px"
                              fontWeight={400}
                              color="white"
                            >
                              for a currently estimated value of{' '}
                              <b
                                style={{
                                  color: 'var(--chakra-colors-orange)',
                                  fontWeight: 400
                                }}
                              >
                                {formatUSD.format(totalUsdAmount)}
                              </b>
                            </Text>
                          )}
                        </HStack>
                      );
                    }
                  }}
                />
              </>
            )
          )}
        </HStack>
      </Center>
      <HStack gap="22px" flexWrap={['wrap', 'unset']} mt={8}>
        {isLoading ? (
          <ListLoader />
        ) : (
          <>
            {uniqBy(distributedTokens, 'address')?.map((tkn, idx) => {
              return (
                <PendingTokenItemPreview
                  key={`${tkn.address}}`}
                  index={idx}
                  distributedTokensIndexes={distributedTokensIndexes}
                  token={tkn}
                  data={data}
                />
              );
            })}
          </>
        )}
      </HStack>
    </Card>
  );
};
